import Archive from '@modules/Archive'
import React, { useEffect, useMemo, useRef, useState } from "react"
import useUpdateEffect from './useUpdateEffect'

export default function useArchive<T extends ObjectWithId>(source: ArchiveData<T> | undefined, config?: ArchiveHookConfig<T>) {
    const [data, setData] = useState<ArchiveData<T>>()
    const [page, setPage] = useState<ArchiveData<T>>()
    const mute = useArchiveMute()

    const filters = useArchiveFilters<T>(source)
    const sort = useArchiveSort<T>()
    const pagination = useArchivePagination(data)
    const { rules, ...useConfig } = useArchiveConfig(config)
    const element = useArchiveElement(page, pagination.info, rules)

    useEffect(() => {
        if (mute.enabled)
            return
        
        setData(sort.apply(filters.apply(source)))
    }, [source, filters.rules, sort.rule])

    useEffect(() => {
        if (mute.enabled)
            return 

        setPage(pagination.apply(data))
    }, [data, pagination.rules])

    useUpdateEffect(() => {
       pagination.info.setCurrent(0)
    }, [filters.rules, sort.rule])

    useEffect(() => {
        mute.unmute()
    })

    return { 
        data,
        element, 
        ...useConfig, 
        sort: sort.add, 
        filter: filters.add,
        usePagination: pagination.use,
        mute: mute.mute,
        options: {
            filters,
            sort,
            pagination
        }
    }
}

function useArchiveMute() {
    let ref = useRef(false)
    const mute = () => ref.current = true
    const unmute = () => ref.current = false
    return { enabled: ref.current, mute, unmute }
}

function useArchiveElement<T extends ObjectWithId>(page: ArchiveData<T>, pagination: ArchivePaginationInterface, config: ArchiveHookConfig<T> | undefined) {
    return useMemo(() => {
        const { component, ...options } = config || {}
        return <Archive data={page} component={component || (() => null)} pagination={pagination} options={options} />
    }, [page, pagination, config])
}

function useArchiveConfig<T extends ObjectWithId>(config: ArchiveHookConfig<T> | undefined) {
    const [rules, setRules] = useState<ArchiveHookConfig<T> | undefined>(config)

    const useComponent = (component: ArchiveComponent<T>) => {
        useEffect(() => {
            setRules(old => ({ ...old, component }))
        }, [])
    }

    const useEmpty = (empty: ArchiveOptions["empty"], deps: any[] = []) => {
        useEffect(() => {
            setRules(old => ({ ...old, empty }))
        }, deps)
    }

    const useOptions = (options: ArchiveOptions) => {
        useEffect(() => {
            setRules(old => ({ ...old, ...options }))
        }, [])
    }

    const useGrid = (grid: ArchiveOptions["grid"]) => {
        useEffect(() => {
            setRules(old => ({ ...old, grid }))
        }, [])
    }

    return { rules, useEmpty, useOptions, useGrid, useComponent }
}

function useArchiveSort<T extends ObjectWithId>() {
    const [rule, setRule] = useState<ArchiveHookSorting<T>>()

    const apply = (data: ArchiveData<T>) => {
        if (!data) return null
        if (!rule) return data
    
        const result = data.sort(rule.fn) 
        if (rule.reverse) result.reverse()
        return [...result]
    }

    const add = (fn: CompareFunction<T>, reverse: boolean = false) => {
        const active = () => {
            return rule?.fn === fn && rule.reverse === reverse
        }

        const set = () => {
            setRule({ fn, reverse })
        }

        const reset = () => {
            setRule(undefined)
        }

        const use = () => {
            useEffect(() => set(), [])
        }

        return { set, reset, active, use }
    }

    return { rule, apply, add }
}

function useArchiveFilters<T extends ObjectWithId>(source: ArchiveData<T>) {
    const [rules, setRules] = useState<ArchiveHookFilterRule<T>[]>([])

    const apply = (source: ArchiveData<T>) => {
        if (!source) return null
    
        let result = source
        for (const rule of rules)
            result = rule.fn(result, rule.value)
        return result
    }

    const add = <V extends any>(group: string, fn: ArchiveHookFilterRule<T, V>["fn"]) => {
        const active = (value: V) => {
            const rule = rules.find(rule => rule.group === group && rule.value === value)
            return Boolean(rule)
        }

        const length = (value: V, filters: boolean = true) => {
            let data = source || []

            if (filters) {
                for (const rule of rules) {
                    if (rule.group === group)
                        continue
                    data = rule.fn(data, rule.value)
                }
            }
            
            return fn(data, value).length
        }

        const set = (value: V) => {
            setRules(old => {
                const filter = { value, group, fn }
                const result = old.filter(rule => rule.group !== group)
                return [ ...result, filter]
            })
        }

        const reset = () => {
            setRules(old => old.filter(rule => rule.group !== group))
        }

        return { set, reset, length, active }
    }

    return { rules, apply, add }
}

function useArchivePagination<T extends ObjectWithId>(data: ArchiveData<T>) {
    const [rules, setRules] = useState({
        enabled: false,
        count: data?.length || 0,
        current: 0,
        label: "items"
    })

    const firstElementOnPage = (rules.count * rules.current) + 1
    const elements = data ? data.length : 0
    const lastElementOnPage = Math.min(rules.count * (rules.current + 1), elements)
    const pages = getPagesCount(data, rules.count)
    const canNext = lastElementOnPage < elements
    const canPrev = firstElementOnPage > 1

    const setCurrent = (current: number) => setRules(old => ({ ...old, current }))
    const next = () => setRules(old => ({ ...old, current: Math.min(old.current + 1, pages - 1) }))
    const prev = () => setRules(old => ({ ...old, current: Math.max(old.current - 1, 0) }))

    const apply = <T extends ObjectWithId>(data: ArchiveData<T>) => {
        if (!data) return null
        if (!rules.enabled) return data
        const firstIdx = rules.count * rules.current
        const lastIdx = Math.min(rules.count * (rules.current + 1), data.length)
        return data.slice(firstIdx, lastIdx)
    }

    const use = (count: number, label: string) => {
        useEffect(() => setRules({ count, label, current: 0, enabled: true }), [])
    }

    const info: ArchivePaginationInterface = {
        ...rules,
        firstElementOnPage,
        lastElementOnPage,
        pages,
        elements,
        canNext, canPrev,
        next, prev, setCurrent
    }

    return useMemo(() => ({ use, apply, rules, info }), [rules, data])
}

// FUNCTIONS
function getPagesCount<T extends ObjectWithId>(data: ArchiveData<T>, count: number) {
    if (!data) return 0
    return Math.ceil(data.length / count)
}